import React, {useState} from 'react';
import {Link, useParams} from "react-router-dom";
import {useQuery} from '@tanstack/react-query';
import secureStorage from "../utils/secureStorage";
import {api} from "../config/api";
import moment from "moment";
import "moment/locale/fr";
import {
    Phone, Mail, Calendar, Tag, ShoppingBag,
    CreditCard, Clock, ArrowLeft, AlertCircle,
    Banknote, CreditCard as CardIcon, PackageSearch,
    FileText, Send, X, Package, User, Eye, Loader2, BookOpen, RefreshCw
} from "lucide-react";
import toast from "react-hot-toast";
import {formatPhone} from '../utils/phone';
import {
    HeaderAction, SummaryStrip, ListLoader, ListEmpty, Panel, thClass, mobileCardClass, Avatar, RowActions
} from '../stock/StockUI';
import Button from '../components/Common/Button';
import ClientAppointments from './ClientAppointments';

moment.locale("fr");

const StatistiquesClients = () => {
    const {clientId} = useParams();
    const [dateRange] = useState({
        startDate: moment().subtract(3, 'months').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD')
    });

    // ── Sale detail / invoice ────────────────────────────────────────────────
    const [selectedSale, setSelectedSale] = useState(null);
    const [saleDetails, setSaleDetails] = useState(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
    const [isSendingInvoiceEmail, setIsSendingInvoiceEmail] = useState(false);
    const [showInvoiceEmailModal, setShowInvoiceEmailModal] = useState(false);
    const [invoiceEmailForm, setInvoiceEmailForm] = useState({email: '', message: ''});

    const fetchSaleDetails = async (saleId) => {
        setIsLoadingDetails(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const res = await api.get(`/activite/sales/${saleId}`, {
                headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
            });
            if (res.data) setSaleDetails(res.data);
        } catch (e) {
            console.error("Error fetching sale details:", e);
            toast.error("Erreur lors du chargement des détails de la vente");
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const openSaleDetail = (sale) => {
        setSelectedSale(sale);
        setSaleDetails(null);
        fetchSaleDetails(sale.id);
    };

    const closeSaleDetail = () => {
        setSelectedSale(null);
        setSaleDetails(null);
        setShowInvoiceEmailModal(false);
        setInvoiceEmailForm({email: '', message: ''});
    };

    const generateInvoice = async (saleId) => {
        setIsGeneratingInvoice(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const res = await api.get(`/generate_invoice/${saleId}`, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            if (res.data?.status === "OK" && res.data?.url) {
                window.open(res.data.url, '_blank');
                toast.success(`Facture ${res.data.invoice_number} générée`);
            } else {
                toast.error("Erreur lors de la génération de la facture");
            }
        } catch (e) {
            console.error("Error generating invoice:", e);
            toast.error(e.response?.data?.message || "Erreur lors de la génération de la facture");
        } finally {
            setIsGeneratingInvoice(false);
        }
    };

    const openInvoiceEmailModal = () => {
        setInvoiceEmailForm({email: saleDetails?.client?.email || '', message: ''});
        setShowInvoiceEmailModal(true);
    };

    const closeInvoiceEmailModal = (force = false) => {
        if (isSendingInvoiceEmail && !force) return;
        setShowInvoiceEmailModal(false);
        setInvoiceEmailForm({email: '', message: ''});
    };

    const sendInvoiceByEmail = async () => {
        const email = invoiceEmailForm.email.trim();
        const message = invoiceEmailForm.message.trim();

        if (!selectedSale?.id) {
            toast.error("Vente introuvable");
            return;
        }
        if (!email) {
            toast.error("L'email du destinataire est obligatoire");
            return;
        }
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(email)) {
            toast.error("Veuillez saisir une adresse email valide");
            return;
        }

        setIsSendingInvoiceEmail(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const payload = {email};
            if (message) payload.message = message;
            const res = await api.post(`/send_invoice/${selectedSale.id}`, payload, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });
            if (res.data?.status === 'OK') {
                toast.success(`Facture ${res.data.invoice_number} envoyée à ${res.data.recipient || email}`);
                closeInvoiceEmailModal(true);
            } else {
                toast.error("Erreur lors de l'envoi de la facture");
            }
        } catch (e) {
            console.error("Error sending invoice by email:", e);
            toast.error(e.response?.data?.message || "Erreur lors de l'envoi de la facture");
        } finally {
            setIsSendingInvoiceEmail(false);
        }
    };

    const calcDiscountMoney = (discount, product) => {
        if (!discount) return 0;
        // API-computed DH amount (price × quantity aware, same formula as the POS).
        if (discount.money != null) return Number(discount.money) || 0;
        const base = product ? (product.price || 0) * (product.quantity || 1) : 0;
        if (discount.type === '%') return base > 0 ? base * discount.value / 100 : discount.value;
        return discount.value || 0;
    };

    /** The API sometimes returns a single `discount` object on a product instead of a `discounts` array. */
    const getProductDiscounts = (product) => {
        if (!product) return [];
        if (Array.isArray(product.discounts)) return product.discounts;
        if (product.discount) return [product.discount];
        return [];
    };

    const validNote = (note) => note && note.trim() !== '' && note.toLowerCase() !== 'undefined';

    const fetchClientStats = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const params = new URLSearchParams({
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
                startTime: '08:00',
                endTime: '23:00'
            }).toString();

            const response = await api.get(`/activite/client/${clientId}?${params}`, {
                headers: {
                    'X-account-id': accountId.id,
                }
            });

            return response.data;
        } catch (error) {
            console.error("Error fetching client stats:", error);
            const errData = error.response?.data;
            // API returns { error: "No sales found for this client", client_id, period }
            // Treat this as a valid "empty" state, not a hard error
            if (errData?.error && errData.error.toLowerCase().includes("no sales found")) {
                return { noSales: true, client_id: errData.client_id, period: errData.period };
            }
            throw new Error(errData?.error || errData?.detail || "Une erreur s'est produite");
        }
    };

    const {
        data: clientStats,
        isLoading,
        isFetching,
        refetch,
        error
    } = useQuery({
        queryKey: ['client-stats', clientId, dateRange],
        queryFn: fetchClientStats,
        enabled: !!clientId
    });

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return moment(dateString).format("DD MMM YYYY, HH:mm");
    };

    const paymentMethodColors = {
        "espece": "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        "chéque": "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
        "carte": "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400",
        "virement": "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",
        "default": "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
    };

    const getPaymentMethodColor = (method) => {
        return paymentMethodColors[method.toLowerCase()] || paymentMethodColors.default;
    };

    const getPaymentIcon = (method) => {
        switch (method.toLowerCase()) {
            case 'espece':
                return <Banknote className="w-4 h-4 mr-1"/>;
            case 'chéque':
                return <CardIcon className="w-4 h-4 mr-1"/>;
            default:
                return <CreditCard className="w-4 h-4 mr-1"/>;
        }
    };

    const {client = {}, metrics = {}, sales = [], payments = [], discounts_summary: discountsSummary = []} =
        (clientStats && !clientStats.noSales) ? clientStats : {};
    const formatDiscountRate = (d) => d.value == null ? '' : d.type === '%' ? `-${d.value}%` : `-${formatCurrency(d.value)} DH`;
    const clientFullName = [client.first_name, client.last_name].filter(Boolean).join(' ');

    const renderBody = () => {
        if (isLoading) return <ListLoader label="Chargement des statistiques..."/>;

        if (error) return (
            <div className="m-4 sm:m-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0"/>
                <div>
                    <p className="font-medium">Erreur lors du chargement des statistiques</p>
                    <p className="text-sm">{error.message}</p>
                </div>
            </div>
        );

        if (!clientStats) return <ListEmpty icon={PackageSearch} title="Aucune donnée disponible"/>;

        // No sales found for this client in the selected period
        if (clientStats.noSales) return (
            <ListEmpty
                icon={PackageSearch}
                title="Aucune vente trouvée"
                text="Ce client n'a effectué aucun achat durant la période sélectionnée."
                action={clientStats.period && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg px-4 py-2 mt-4">
                        <Calendar className="w-3.5 h-3.5 shrink-0"/>
                        <span>
                            {moment(clientStats.period.start_date).format("DD MMM YYYY")}
                            {" · "}
                            {moment(clientStats.period.end_date).format("DD MMM YYYY")}
                        </span>
                    </div>
                )}
            />
        );

        return (<>
            {/* Client identity */}
            <div className="flex items-center gap-4 px-4 sm:px-8 py-4 border-b border-gray-200 dark:border-gray-700">
                <Avatar name={`${client.first_name?.charAt(0) || ''}${client.last_name?.charAt(0) || ''}` || '?'} size="lg"/>
                <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {clientFullName || 'Sans nom'}
                    </h2>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {client.phone && (
                            <span className="flex items-center gap-1.5">
                                <Phone className="w-4 h-4 text-gray-400"/>
                                {formatPhone(client.countryCode, client.phone)}
                            </span>
                        )}
                        {client.email && (
                            <span className="flex items-center gap-1.5 min-w-0">
                                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                                <span className="truncate">{client.email}</span>
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Metrics */}
            <SummaryStrip items={[
                {key: 'spent', label: 'Total dépensé', value: `${formatCurrency(metrics.total_spent)} DH`},
                {key: 'count', label: "Nombre d'achats", value: metrics.sales_count ?? 0, color: 'text-primary'},
                {key: 'avg', label: 'Valeur moy. achat', value: `${formatCurrency(metrics.average_sale_value)} DH`},
                {key: 'discount', label: 'Total des remises', value: `${formatCurrency(metrics.total_discount)} DH`, color: 'text-red-600 dark:text-red-400'},
            ]}/>

            <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Payment Methods Summary */}
                <Panel title="Modes de paiement">
                    {payments.length === 0 ? (
                        <p className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">Aucun paiement enregistré</p>
                    ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {payments.map((payment, index) => (
                                <div key={payment.type + index} className="flex items-center justify-between px-4 sm:px-6 py-3">
                                    <div className="flex items-center gap-3">
                                        <span className={`rounded-lg p-2 ${getPaymentMethodColor(payment.type)}`}>
                                            {getPaymentIcon(payment.type)}
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">{payment.type}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {payment.count} transaction{payment.count > 1 ? 's' : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(payment.total)} DH</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {metrics.total_spent ? Math.round((payment.total / metrics.total_spent) * 100) : 0}% du total
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>

                {/* Important dates */}
                <Panel title="Dates importantes">
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {[
                            {label: 'Premier achat', value: metrics.first_purchase_date},
                            {label: 'Dernier achat', value: metrics.last_purchase_date},
                        ].map(row => (
                            <div key={row.label} className="flex items-center gap-3 px-4 sm:px-6 py-3">
                                <span className="p-2 rounded-lg bg-primary/10 text-primary"><Clock className="w-4 h-4"/></span>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{row.label}</p>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatDate(row.value)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>

            {/* Applied discounts */}
            <Panel
                title="Remises appliquées"
                aside={
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {metrics.discounted_sales_count || 0} achat(s) remisé(s) ·{' '}
                        <span className="font-semibold text-red-600 dark:text-red-400">-{formatCurrency(metrics.total_discount)} DH</span>
                    </span>
                }
            >
                {discountsSummary.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {discountsSummary.map((d, i) => (
                            <div key={d.discount_id || i} className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="p-2 rounded-lg bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 flex-shrink-0">
                                        <Tag className="w-4 h-4"/>
                                    </span>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                            {d.name} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">{formatDiscountRate(d)}</span>
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {d.sales_count} achat{d.sales_count > 1 ? 's' : ''} · {d.lines_count} article{d.lines_count > 1 ? 's' : ''} · dernière fois le {formatDate(d.last_used_at)}
                                        </p>
                                    </div>
                                </div>
                                <p className="font-semibold text-sm text-red-600 dark:text-red-400 whitespace-nowrap">
                                    -{formatCurrency(d.total)} DH
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
                        {metrics.total_discount > 0
                            ? "Remises globales uniquement (pas de détail par remise)"
                            : "Aucune remise appliquée à ce client"}
                    </p>
                )}
            </Panel>

            {/* Purchase history */}
            <Panel title="Historique des achats" aside={
                <span className="text-xs text-gray-500 dark:text-gray-400">{sales.length} vente{sales.length > 1 ? 's' : ''}</span>
            }>
                {sales.length === 0 ? (
                    <ListEmpty icon={ShoppingBag} title="Aucune vente"
                               text="Aucune vente trouvée pour ce client dans la période sélectionnée."/>
                ) : (<>
                    {/* Desktop Table */}
                    <div className="hidden lg:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                                <th className={thClass}>Date</th>
                                <th className={thClass}>Montant</th>
                                <th className={thClass}>Remise</th>
                                <th className={thClass}>Produits</th>
                                <th className={thClass}>Employé</th>
                                <th className={thClass}>Paiement</th>
                                <th className={thClass}>Statut</th>
                                <th className={`${thClass} text-right`}>Actions</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {sales.map(sale => (
                                <tr key={sale.id} onClick={() => openSaleDetail(sale)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            {moment(sale.createdAt).format("DD MMM YYYY")}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {moment(sale.createdAt).format("HH:mm")}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        {formatCurrency(sale.total)} DH
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {sale.discount_total > 0 ? (
                                            <span className="text-sm font-medium text-red-600 dark:text-red-400">
                                                -{formatCurrency(sale.discount_total)} DH
                                            </span>
                                        ) : (
                                            <span className="text-gray-300 dark:text-gray-600">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
                                            {(sale.products || []).map(p => p.name).join(", ")}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {(sale.products || []).length} article(s)
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                        {sale.created_employee?.name || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {(sale.payments || []).map(payment => (
                                                <span key={payment.id}
                                                      className={`text-xs py-1 px-2 rounded-full font-medium flex items-center ${getPaymentMethodColor(payment.name)}`}>
                                                    {getPaymentIcon(payment.name)}
                                                    {formatCurrency(payment.amount)} DH
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                            {sale.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right" onClick={e => e.stopPropagation()}>
                                        <RowActions actions={[
                                            {label: 'Voir le détail', icon: Eye, onClick: () => openSaleDetail(sale)},
                                            {label: 'Facture', icon: FileText, tone: 'green', onClick: () => generateInvoice(sale.id)},
                                        ]}/>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="lg:hidden p-4 space-y-3">
                        {sales.map(sale => (
                            <div key={sale.id} onClick={() => openSaleDetail(sale)} className={`${mobileCardClass} cursor-pointer`}>
                                <div className="flex justify-between items-start gap-3">
                                    <div>
                                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                            {formatCurrency(sale.total)} DH
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(sale.createdAt)}</p>
                                    </div>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                        {sale.status}
                                    </span>
                                </div>
                                <div className="text-sm mt-3 space-y-0.5">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        <span className="font-medium">Produits :</span> {(sale.products || []).length}
                                    </p>
                                    {sale.discount_total > 0 && (
                                        <p className="text-red-600 dark:text-red-400">
                                            <span className="font-medium">Remise :</span> -{formatCurrency(sale.discount_total)} DH
                                        </p>
                                    )}
                                    <p className="text-gray-500 dark:text-gray-400">
                                        <span className="font-medium">Employé :</span> {sale.created_employee?.name || '-'}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {(sale.payments || []).map(payment => (
                                        <span key={payment.id}
                                              className={`text-xs py-1 px-2 rounded-full font-medium flex items-center ${getPaymentMethodColor(payment.name)}`}>
                                            {getPaymentIcon(payment.name)}
                                            {payment.name} ({formatCurrency(payment.amount)} DH)
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </>)}
            </Panel>
        </>);
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200">
            {/* Header — same layout as the catalogue forms */}
            <header className="flex justify-between items-center px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700">
                <Link to={`/clients/edit/${clientId}`}
                      className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 flex items-center gap-1 sm:gap-2">
                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5"/>
                    <span className="text-xs sm:text-sm font-medium">Retour</span>
                </Link>
                <h1 className="text-base sm:text-lg lg:text-xl font-semibold mx-2 sm:mx-4 flex-1 text-center text-gray-800 dark:text-white">
                    Statistiques client
                </h1>
                <HeaderAction
                    icon={RefreshCw}
                    loading={isFetching}
                    title="Actualiser"
                    label={isFetching ? 'Actualisation...' : 'Actualiser'}
                    onClick={() => refetch()}
                />
            </header>

            {renderBody()}

            {/* Outside renderBody: a client may have appointments without any sale */}
            <ClientAppointments clientId={clientId}/>

            {/* ── Sale detail modal ────────────────────────────────────────── */}
            {selectedSale && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
                    onClick={closeSaleDetail}
                >
                    <div
                        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-4 sm:p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    Vente #{selectedSale.id.slice(0, 8)}
                                </span>
                                <div className="flex items-center gap-2">
                                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                                        {selectedSale.status || 'Terminé'}
                                    </span>
                                    <button
                                        onClick={closeSaleDetail}
                                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <X className="w-5 h-5 text-gray-500 dark:text-gray-400"/>
                                    </button>
                                </div>
                            </div>

                            {/* Hero: total + meta + actions */}
                            <div className="text-center pb-6 mb-5 border-b border-gray-200 dark:border-gray-700">
                                <h1 className="text-3xl sm:text-4xl font-bold mt-1 text-gray-900 dark:text-gray-100">
                                    {formatCurrency(selectedSale.total)} DH
                                </h1>
                                <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">
                                    {(selectedSale.products || []).length} article(s) · {formatDate(selectedSale.createdAt)}
                                </p>
                                <div className="flex items-center justify-center gap-3 mt-4">
                                    <Button variant="primary" icon={FileText} loading={isGeneratingInvoice}
                                            title="Générer la facture" onClick={() => generateInvoice(selectedSale.id)}>
                                        {isGeneratingInvoice ? 'Génération...' : 'Facture'}
                                    </Button>
                                    <Button variant="tinted" icon={Send} loading={isSendingInvoiceEmail}
                                            disabled={isLoadingDetails} title="Envoyer la facture par email"
                                            onClick={openInvoiceEmailModal}>
                                        {isSendingInvoiceEmail ? 'Envoi...' : 'Envoyer email'}
                                    </Button>
                                </div>
                            </div>

                            {isLoadingDetails && (
                                <div className="flex items-center justify-center py-16">
                                    <div className="text-center">
                                        <div className="w-8 h-8 border-4 border-blue-200 border-t-primary rounded-full animate-spin mx-auto mb-3"/>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Chargement des détails...</p>
                                    </div>
                                </div>
                            )}

                            {saleDetails && !isLoadingDetails && (
                                <div className="space-y-5">
                                    {/* Employee / Device / Client cards */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                                            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                                                <User className="w-4 h-4"/>Employé
                                            </h3>
                                            <div className="space-y-1 text-sm">
                                                <p><span className="text-gray-500 dark:text-gray-400">Créé par:</span> <span className="font-medium ml-1">{saleDetails.created_employee?.name || 'N/A'}</span></p>
                                                {saleDetails.updated_employee && (
                                                    <p><span className="text-gray-500 dark:text-gray-400">Modifié par:</span> <span className="font-medium ml-1">{saleDetails.updated_employee.name}</span></p>
                                                )}
                                            </div>
                                        </div>
                                        {saleDetails.client && (
                                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                                                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                                                    <User className="w-4 h-4"/>Client
                                                </h3>
                                                <div className="space-y-1 text-sm">
                                                    <p className="font-medium text-gray-900 dark:text-gray-100">{saleDetails.client.first_name} {saleDetails.client.last_name}</p>
                                                    {saleDetails.client.phone && (
                                                        <p className="flex items-center gap-1 text-gray-700 dark:text-gray-300"><Phone className="w-3 h-3"/>{formatPhone(saleDetails.client.countryCode, saleDetails.client.phone)}</p>
                                                    )}
                                                    {saleDetails.client.email && (
                                                        <p className="flex items-center gap-1 text-gray-700 dark:text-gray-300"><Mail className="w-3 h-3"/>{saleDetails.client.email}</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Products */}
                                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                                            <h3 className="font-semibold text-sm flex items-center gap-2 text-gray-900 dark:text-gray-100">
                                                <Package className="w-4 h-4"/>Articles ({saleDetails.products?.length || 0})
                                            </h3>
                                        </div>
                                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {saleDetails.products?.map((product, i) =>
                                                product.is_menu ? (
                                                    <div key={i} className="p-3 sm:p-4">
                                                        <div className="flex items-center justify-between mb-2.5">
                                                            <div className="flex items-center gap-2.5">
                                                                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-teal-100 dark:bg-teal-900/30">
                                                                    <BookOpen className="w-4 h-4 text-teal-600 dark:text-teal-400"/>
                                                                </div>
                                                                <div>
                                                                    <p className="font-semibold text-sm text-teal-800 dark:text-teal-300">{product.menu_name}</p>
                                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                                        Menu · {product.menu_items?.length || 0} article(s)
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <p className="font-semibold text-sm whitespace-nowrap text-teal-700 dark:text-teal-300">
                                                                {product.total?.toFixed(2)} DH
                                                            </p>
                                                        </div>
                                                        <div className="ml-10 space-y-1.5">
                                                            {product.menu_items?.map((item, ii) => (
                                                                <div key={ii} className="flex items-center gap-2 rounded-lg px-2.5 py-2 bg-gray-50 dark:bg-gray-900/70">
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-medium truncate text-gray-700 dark:text-gray-300">{item.name}</p>
                                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{item.price?.toFixed(2)} DH × {item.quantity}</p>
                                                                    </div>
                                                                    <p className="text-xs font-semibold whitespace-nowrap flex-shrink-0 text-gray-700 dark:text-gray-300">
                                                                        {item.total?.toFixed(2)} DH
                                                                    </p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div key={i} className="p-3 sm:p-4 flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            {product.image ? (
                                                                <img src={product.image} alt={product.name}
                                                                     className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600 flex-shrink-0"
                                                                     onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                                                                />
                                                            ) : (
                                                                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-gray-700">
                                                                    <Package className="w-6 h-6 text-gray-400 dark:text-gray-500"/>
                                                                </div>
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium truncate text-sm text-gray-900 dark:text-gray-100">{product.name}</p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">{(product.price || 0).toFixed(2)} DH × {product.quantity}</p>
                                                                {getProductDiscounts(product).length > 0 && (
                                                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                                                        {getProductDiscounts(product).map((d, di) => {
                                                                            const money = calcDiscountMoney(d, product);
                                                                            return (
                                                                                <span key={di} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border bg-red-50 border-red-100 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                                                                                    <Tag className="w-2.5 h-2.5 flex-shrink-0"/>
                                                                                    {d.name} {d.type === '%' ? `(-${d.value}% soit -${money.toFixed(2)} DH)` : `(-${money.toFixed(2)} DH)`}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="text-right flex-shrink-0">
                                                            {getProductDiscounts(product).length > 0 ? (
                                                                <>
                                                                    <p className="font-semibold text-sm whitespace-nowrap text-gray-900 dark:text-gray-100">
                                                                        {(product.total - getProductDiscounts(product).reduce((s, d) => s + calcDiscountMoney(d, product), 0)).toFixed(2)} DH
                                                                    </p>
                                                                    <p className="text-xs line-through whitespace-nowrap text-gray-500 dark:text-gray-400">
                                                                        {product.total?.toFixed(2)} DH
                                                                    </p>
                                                                </>
                                                            ) : (
                                                                <p className="font-semibold text-sm whitespace-nowrap text-gray-900 dark:text-gray-100">{product.total?.toFixed(2)} DH</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {saleDetails.discount_total > 0 && (
                                        <div className="rounded-xl border border-red-100 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-center justify-between">
                                            <p className="text-sm font-medium flex items-center gap-2 text-red-700 dark:text-red-300">
                                                <Tag className="w-4 h-4"/>Total des remises
                                            </p>
                                            <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                                                -{formatCurrency(saleDetails.discount_total)} DH
                                            </p>
                                        </div>
                                    )}

                                    {/* Payments */}
                                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                                            <h3 className="font-semibold text-sm flex items-center gap-2 text-gray-900 dark:text-gray-100">
                                                <CreditCard className="w-4 h-4"/>Paiements ({saleDetails.payments?.length || 0})
                                            </h3>
                                        </div>
                                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                            {saleDetails.payments?.map((payment, i) => (
                                                <div key={i} className="p-3 sm:p-4 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getPaymentMethodColor(payment.name)}`}>
                                                            {getPaymentIcon(payment.name)}
                                                        </div>
                                                        <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{payment.name}</p>
                                                    </div>
                                                    <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{formatCurrency(payment.amount)} DH</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {validNote(saleDetails.note) && (
                                        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                                            <p className="text-xs font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400 mb-1">Note</p>
                                            <p className="text-sm text-amber-800 dark:text-amber-300">{saleDetails.note}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Invoice email modal ──────────────────────────────────────── */}
            {showInvoiceEmailModal && selectedSale && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4" onClick={() => closeInvoiceEmailModal()}>
                    <div
                        className="w-full max-w-lg rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Envoyer la facture par email</h3>
                                <p className="text-sm mt-0.5 text-gray-500 dark:text-gray-400">Vente #{selectedSale.id.slice(0, 8)}</p>
                            </div>
                            <button
                                onClick={() => closeInvoiceEmailModal()}
                                disabled={isSendingInvoiceEmail}
                                className="p-2 rounded-lg transition-colors disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <X className="w-5 h-5 text-gray-500 dark:text-gray-400"/>
                            </button>
                        </div>

                        <div className="px-5 py-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">Email du destinataire</label>
                                <input
                                    type="email"
                                    value={invoiceEmailForm.email}
                                    onChange={(e) => setInvoiceEmailForm(prev => ({...prev, email: e.target.value}))}
                                    placeholder="client@example.com"
                                    className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                                />
                                {saleDetails?.client?.email && (
                                    <p className="mt-1.5 text-xs flex items-center gap-1 text-gray-500 dark:text-gray-400">
                                        <Mail className="w-3.5 h-3.5"/>
                                        Email client détecté : {saleDetails.client.email}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">Message personnalisé</label>
                                <textarea
                                    rows={5}
                                    value={invoiceEmailForm.message}
                                    onChange={(e) => setInvoiceEmailForm(prev => ({...prev, message: e.target.value}))}
                                    placeholder={"Bonjour,\n\nVeuillez trouver ci-joint votre facture.\n\nCordialement,"}
                                    className="w-full rounded-lg border px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                                />
                                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                                    Laisser vide pour utiliser le message par défaut de l'API.
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/70">
                            <button
                                onClick={() => closeInvoiceEmailModal()}
                                disabled={isSendingInvoiceEmail}
                                className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:opacity-50 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={sendInvoiceByEmail}
                                disabled={isSendingInvoiceEmail}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSendingInvoiceEmail ? (
                                    <Loader2 className="w-4 h-4 animate-spin"/>
                                ) : (
                                    <Send className="w-4 h-4"/>
                                )}
                                {isSendingInvoiceEmail ? 'Envoi en cours...' : 'Envoyer la facture'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatistiquesClients;
